import { useState, useEffect, useMemo } from "react";
import FilterSectionForComparativeAnalysis from "./FilterSectionForComparativeAnalysis";
import LocationPerformanceComparisonChart from "./LocationPerformanceComparisonChart";
import YearOverYearComparisonChart from "./YearOverYearComparisonChart";
import { Col, Row } from "react-bootstrap";
import { getComparativeAnalysisData } from "../../Training/training-dashboard/services/trainingService";
import { getStartingMonth } from "../../CarbonFootPrinting/utils/PeriodCalculationUtils";

const ComparativeAnalysis = ({
  financialYear = [],
  moduleOptions = [],
  locations = [],
  periodOptions = [],
  locationOptions = [],
  frequency = "MONTHLY"
}) => {
  const [selectedFinancialYears, setSelectedFinancialYears] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedPrimaryLocation, setSelectedPrimaryLocation] = useState('');
  const [selectedCompareLocation, setSelectedCompareLocation] = useState('');

  // Comparative data states
  const [comparativeData, setComparativeData] = useState({
    locationComparison: null,
    yearOverYear: null,
    kpiComparison: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set default primary and compare locations
  useEffect(() => {
    if (!selectedPrimaryLocation && locations?.length > 0) {
      setSelectedPrimaryLocation(locations[0].id);
    }
  }, [selectedPrimaryLocation, locations]);

  useEffect(() => {
    if (!selectedCompareLocation && locations?.length > 0) {
      setSelectedCompareLocation(locations[1]?.id || locations[0].id);
    }
  }, [selectedCompareLocation, locations]);

  // Set default selected financial years — latest + previous
  useEffect(() => {
    if (financialYear && financialYear.length > 0) {
      // Sort descending to get the latest FY first
      const sortedDesc = [...financialYear].sort(
        (a, b) => String(b.financial_year_value).localeCompare(String(a.financial_year_value))
      );
      // Pick latest + previous (max 2)
      const defaultIds = sortedDesc.slice(0, 2).map(fy => fy.id);
      setSelectedFinancialYears(defaultIds);
    }
  }, [financialYear]);

  const financialYearOptions = financialYear.map(year => ({
    value: year.id,
    label: year.financial_year_value || year.name || `Year ${year.id}`
  }));

  /**
   * Clears all filters back to defaults.
   */
  const handleClearFilters = () => {
    if (financialYear && financialYear.length > 0) {
      // Sort descending — latest + previous
      const sortedDesc = [...financialYear].sort(
        (a, b) => String(b.financial_year_value).localeCompare(String(a.financial_year_value))
      );
      const defaultIds = sortedDesc.slice(0, 2).map(fy => fy.id);
      setSelectedFinancialYears(defaultIds);
    } else {
      setSelectedFinancialYears([]);
    }
    setSelectedModules([]);
    if (locations?.length > 0) {
      setSelectedPrimaryLocation(locations[0].id);
      setSelectedCompareLocation(locations[1]?.id || locations[0].id);
    } else {
      setSelectedPrimaryLocation('');
      setSelectedCompareLocation('');
    }
  };

  /**
   * Returns a map of { fyId => "2023-2024" } for label resolution.
   * @returns {Record<number, string>}
   */
  const fyLabelMap = useMemo(() => {
    return financialYear.reduce((acc, fy) => {
      acc[fy.id] = fy.financial_year_value || fy.name || `Year ${fy.id}`;
      return acc;
    }, {});
  }, [financialYear]);

  /**
   * Gets the human-readable FY label for a given id (e.g. "2023-2024").
   * @param {number|string} id
   * @returns {string|null}
   */
  const getFinancialYearById = (id) => {
    const result = financialYear.find(item => item.id == id);
    return result ? result.financial_year_value : null;
  };

  const calculateDateRange = (type, period, startingMonth, year) => {
    const startMonth = ((startingMonth - 1 + (period - 1) * type) % 12) + 1;
    const startYear = year + Math.floor((startingMonth - 1 + (period - 1) * type) / 12);
    const endMonth = ((startMonth - 1 + type) % 12) + 1;
    const endYear = startYear + Math.floor((startMonth - 1 + type) / 12);

    const formatDate = (month, year) =>
      `${year}-${month < 10 ? `0${month}` : month}`;
    const showLevel = periodOptions.find(q => q.value == period)?.label;
    return {
      fromDate: formatDate(startMonth, startYear),
      toDate: formatDate(endMonth, endYear),
      showLevel: showLevel
    };
  };

  const calculateRange = (financialYearId) => {
    const selectedYear = getFinancialYearById(financialYearId);
    if (!selectedYear) return [];
    const year = parseInt(selectedYear.split("-")[0]);
    const start = getStartingMonth();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const dateRanges = periodOptions.map(item => item.value).map(item => {
      let dateRange;
      const period = Number(item);

      if (frequency === "HALF_YEARLY") {
        dateRange = calculateDateRange(6, period, start, year);
      } else if (frequency === "QUARTERLY") {
        dateRange = calculateDateRange(3, period, start, year);
      } else if (frequency === "MONTHLY") {
        const startIndex = start - 1;
        const firstMonthAbsoluteIndex = start + period - 2;
        const firstMonthIndex = ((firstMonthAbsoluteIndex - startIndex + months.length) % months.length) + 1;

        dateRange = calculateDateRange(1, firstMonthIndex, start, year);
        const showLevel = periodOptions.find(q => q.value == period)?.label;
        dateRange.showLevel = showLevel;
      } else if (frequency === "YEARLY") {
        dateRange = calculateDateRange(12, 1, start, year);
      }
      return dateRange;
    });
    return dateRanges;
  };

  useEffect(() => {
    let isMounted = true;
    const fetchComparativeData = async () => {
      if (selectedFinancialYears.length === 0 || !selectedPrimaryLocation || !selectedCompareLocation) {
        if (isMounted) {
          setComparativeData({
            locationComparison: null,
            yearOverYear: null,
            kpiComparison: null
          });
        }
        return;
      }

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        const lastYearId = selectedFinancialYears[selectedFinancialYears.length - 1];
        const selectedPeriodsValue = calculateRange(lastYearId);

        const data = await getComparativeAnalysisData(
          selectedFinancialYears,
          selectedPrimaryLocation,
          selectedCompareLocation,
          selectedModules,
          selectedPeriodsValue
        );

        if (data && isMounted) {
          /**
           * Replace raw series names (which may be IDs) with human-readable FY labels.
           * The backend returns series in the same order as financialYearIds.
           */
          const patchSeriesNames = (chartData, fyIds) => {
            if (!chartData?.series) return chartData;
            return {
              ...chartData,
              series: chartData.series.map((s, i) => ({
                ...s,
                name: fyLabelMap[fyIds[i]] || s.name,
              })),
            };
          };

          setComparativeData({
            locationComparison: data.locationComparison,
            yearOverYear: patchSeriesNames(data.yearOverYear, selectedFinancialYears),
            kpiComparison: data.kpiComparison
          });
        }
      } catch (err) {
        console.error("Error loading comparative analysis data:", err);
        if (isMounted) {
          setError("Failed to load comparative analysis data.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchComparativeData();
    return () => {
      isMounted = false;
    };
  }, [selectedFinancialYears, selectedModules, selectedPrimaryLocation, selectedCompareLocation, periodOptions, frequency]);

  return (
    <div className="w-full min-h-screen bg-gray-50 py-4">
      {/* Filter Section */}
      <FilterSectionForComparativeAnalysis
        financialYearOptions={financialYearOptions}
        selectedFinancialYears={selectedFinancialYears}
        setSelectedFinancialYears={setSelectedFinancialYears}


        primaryLocationOptions={locations}
        selectedPrimaryLocation={selectedPrimaryLocation}
        setSelectedPrimaryLocation={setSelectedPrimaryLocation}

        compareLocationOptions={locations}
        selectedCompareLocation={selectedCompareLocation}
        setSelectedCompareLocation={setSelectedCompareLocation}

        onClearFilters={handleClearFilters}
      />

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-red-800 font-medium">Error: {error}</span>
          </div>
        </div>
      )}

      <div className="space-y-8">
        <Row>
          <Col md={6}>
            <LocationPerformanceComparisonChart
              chartData={comparativeData.locationComparison}
              loading={loading}
            />
          </Col>
          <Col md={6}>
            <YearOverYearComparisonChart
              chartData={comparativeData.yearOverYear}
              loading={loading}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ComparativeAnalysis;